import React from 'react';
import { Route, Routes, Outlet, Navigate, useNavigate } from 'react-router-dom';

import { useKeycloak } from '@react-keycloak/web';
import { useUnit } from 'effector-react';
import { $user } from 'models/user';
import Home from 'pages/Home';
import useFetch, { CachePolicies, Provider } from 'use-http';
import Photo from 'pages/Photo';
import AdminLayout from 'components/AdminLayout';
import Layout from 'components/Layout';
import Stores from 'pages/admin/Stores';
import moment from 'moment';
import { /*$store, */ $storeList, $storeStr /*, setStore*/, setStoreList } from 'models/store';
import { IStore, IStoreItem } from 'dto/store.dto';
import { GroupsEnum } from 'types/users';
import Forbidden from 'components/Forbidden';
import Login from 'components/Login';
import NotFound from 'components/NotFound';
import Places from 'pages/Places';
import SupportForm from 'components/SupportForm';

import Passport from 'pages/admin/Passport';
import PassportList from 'pages/Passport';
import PassportDetail from 'pages/admin/PassportDetail';
import PlacesAdmin from 'pages/admin/Places';
import EditPlace from 'pages/admin/EditPlace';
import ReportPage from 'pages/admin/Report';
import Banners from 'pages/admin/Banners';
import StoreStatuses from 'pages/admin/StoreStatuses';
import StoreStatusesByManager from 'pages/admin/StoreStatusesByManager';
import Counterparty from 'pages/admin/Counterparty';
import Example from 'pages/admin/Example';
import Section from 'pages/Section';
import ConfirmSection from 'pages/Section/ConfirmSection';
import useLocalStorage from 'use-local-storage';
import { isRd } from 'utils/helpers';

moment.locale('ru');

const host = process.env.REACT_APP_BACKEND_URL;

const ProtectedRoute = ({
	isAllowed = false,
	redirectPath = `/login`,
	children,
}: {
	isAllowed?: boolean;
	redirectPath?: string;
	children?: React.ReactNode;
}) => {
	const { keycloak } = useKeycloak();

	const encodedRef = encodeURIComponent(location.pathname + location.search);
	const user = useUnit($user);
	const storeStr = useUnit($storeStr);
	const storeList = useUnit($storeList);
	const { get } = useFetch();
	const [store, setStore] = useLocalStorage<IStore | undefined>(`store`, undefined);

	// Парсит строку магазинов из storeStr
	const parseStoresFromString = (storeStr: string): IStoreItem[] => {
		try {
			const correctedJsonString = storeStr.replace(/'/g, '"');
			return JSON.parse(correctedJsonString);
		} catch (e) {
			console.log('Error parsing storeStr:', e);
			return [];
		}
	};

	// Загружает список магазинов по их ID
	const loadStoresList = async (storesObject: IStoreItem[]): Promise<IStore[]> => {
		const storesList = [];
		for (const storeItem of storesObject) {
			if (storeItem.storeId) {
				try {
					const storeData = await get(`/api/stores/num/${storeItem.storeId}`);
					storesList.push(storeData);
				} catch (e) {
					console.log(`Error loading store ${storeItem.storeId}:`, e);
				}
			}
		}
		return storesList;
	};

	// Устанавливает приоритетный магазин если он не выбран
	const setPriorityStoreIfNeeded = async (storesObject: IStoreItem[]) => {
		if (store) return; // Если магазин уже выбран, ничего не делаем
		
		const priorityStore = storesObject.find((item: IStoreItem) => +item.position_type === 1);
		if (priorityStore?.storeId) {
			try {
				const storeData = await get(`/api/stores/num/${priorityStore.storeId}`);
				setStore(storeData);
			} catch (e) {
				console.log(`Error loading priority store ${priorityStore.storeId}:`, e);
			}
		}
	};

	React.useEffect(() => {
		async function loadStore() {
			// Проверяем, является ли пользователь клиентом
			const isClient = user && Array.isArray(user.groups) && user.groups.indexOf(GroupsEnum.CLIENT) > -1;
			if (!isClient || !storeStr) return;

			const storesObject = parseStoresFromString(storeStr);
			if (!storesObject.length) return;

			try {
				const storesList = await loadStoresList(storesObject);
				setStoreList(storesList);
				
				await setPriorityStoreIfNeeded(storesObject);
				
				// Если нет загруженных магазинов, сбрасываем выбранный магазин
				if (storesList.length === 0) {
					setStore(undefined);
				}
			} catch (e) {
				console.log('Error in loadStore:', e);
			}
		}

		if (user) {
			loadStore();
		}
	}, [user, storeStr]);

	console.log({ storeList, store, storeStr });

	console.log('ProtectedRoute', { isAllowed, user, redirectPath, authenticated: keycloak.authenticated, encodedRef });
	if (!user) {
		return <Navigate to={redirectPath + `?ref=${encodedRef}`} replace />;
	}
	if (!keycloak?.authenticated) {
		return <Navigate to={redirectPath + `?ref=${encodedRef}`} replace />;
	}
	if (!isAllowed) {
		return <Forbidden />;
	}

	return React.isValidElement(children) ? children : <Outlet />;
};

const allowedGroups = [
    GroupsEnum.ADMIN,
    GroupsEnum.ACCOUNTANT,
    GroupsEnum.SIGNER,
    GroupsEnum.REPORT,
    GroupsEnum.MARKETER
];

function AppRouter() {
	const user = useUnit($user);
	const navigate = useNavigate();

	const { initialized, keycloak } = useKeycloak();

	React.useEffect(() => {
		const hasPermission = (user && Array.isArray(user.groups) && user.groups.some(group => allowedGroups.includes(group as GroupsEnum))) ?? false;		
		if (
			user &&
			Array.isArray(user.groups) &&
			hasPermission &&
			user.groups.indexOf(GroupsEnum.PASSPORT_UPLOAD) === -1 &&
			!/admin/.test(window.location.pathname)
		) {
			navigate('/admin');
		}
	}, [user]);

	if (!initialized) {
		return <div>Загрузка...</div>;
	}

	const globalOptions = {
		cachePolicy: CachePolicies.NO_CACHE,
		interceptors: {
			request: ({ options }: { options: RequestInit }) => {
				const newOptions = options;
				newOptions.headers = {
					Authorization: `Bearer ${keycloak.token}`,
					...options.headers,
				};
				return newOptions;
			},
		},
	};

	const allGroups = Object.values(GroupsEnum);

	return (
		<Provider url={host} options={globalOptions}>
			<Routes>
				<Route
					element={
						<ProtectedRoute
							isAllowed={!!user && Array.isArray(user.groups) && user.groups.indexOf(GroupsEnum.PASSPORT_UPLOAD) > -1}
						/>
					}
				>
					<Route
						path=""
						element={
							<Layout>
								<Home />
							</Layout>
						}
					/>
					<Route
						path="section"
						element={
							<Layout>
								<Section />
							</Layout>
						}
					/>
					<Route
						path="section/confirm/:storeId"
						element={
							<Layout>
								<ConfirmSection />
							</Layout>
						}
					/>
					<Route
						path="places"
						element={
							<Layout>
								<Places />
							</Layout>
						}
					/>
					<Route
						path="photo/:passportId/:placeId"
						element={
							<Layout>
								<Photo />
							</Layout>
						}
					/>
					<Route
						path="support"
						element={
							<Layout>
								<SupportForm />
							</Layout>
						}
					/>
					<Route
						path="passports"
						element={
							<Layout>
								<PassportList />
							</Layout>
						}
					/>
				</Route>

				<Route
					path="/admin"
					element={
						<ProtectedRoute
							isAllowed={
								!!user &&
								Array.isArray(user.groups) &&
								!!user.groups.length &&
								user.groups.some(group => {
									if (group === GroupsEnum.CLIENT) {
										return false;
									}

									return allGroups.includes(group as GroupsEnum);
								})
							}
						/>
					}
				>
					<Route element={<AdminLayout />}>
						<Route path="" element={<Stores />} />
						<Route path="stores/:emailManager/statuses" element={<StoreStatusesByManager />} />
						<Route path="stores/statuses" element={<StoreStatuses />} />
						<Route path="stores/:storeId/counterparty/:counterpartyId" element={<Passport />} />
						<Route path="stores/:storeId/places" element={<PlacesAdmin />} />
						<Route path="stores/:storeId/passport/:passportId" element={<PassportDetail />} />
						<Route path="banners" element={<Banners />} />
						<Route
							path="report"
							element={
								<ProtectedRoute isAllowed={user?.groups?.includes(GroupsEnum.REPORT) || !isRd(user?.groups)}>
									<ReportPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="places/:id"
							element={
								<ProtectedRoute isAllowed={user?.groups?.includes(GroupsEnum.ADMIN)}>
									<EditPlace />
								</ProtectedRoute>
							}
						/>
						<Route path="directory/example" element={<Example />} />
						<Route path="directory/counterparty" element={<Counterparty />} />
					</Route>
				</Route>

				<Route path="login" element={<Login />} />
				<Route path="*" element={<NotFound />} />
			</Routes>
		</Provider>
	);
}

export default AppRouter;
